// src/pages/LearningHub.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Play,
  X,
  Info,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Tag,
  ExternalLink,
} from "lucide-react";

/* ===========================
   Config
=========================== */
const MANIFEST_URL = "/Learning/manifest.json";
const MAGAZINE_URL = "https://online.flippingbook.com/view/784475944/6/";

/* PUBLIC_URL-safe helper (works in CRA/webpack) */
const BASE_URL = (process.env.PUBLIC_URL || "/").replace(/\/$/, "");
const toPublic = (p) => {
  if (!p) return p;
  if (/^https?:\/\//i.test(p)) return p;
  return `${BASE_URL}${p.startsWith("/") ? "" : "/"}${p}`;
};
const isExternal = (url) => /^https?:\/\//i.test(url);

/* ===========================
   Accessibility: Focus trap for modal
=========================== */
function useFocusTrap(enabled, containerRef) {
  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    const root = containerRef.current;
    const focusables = root.querySelectorAll(
      'a,button,input,textarea,select,details,[tabindex]:not([tabindex="-1"])'
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    function onKeyDown(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
    root.addEventListener("keydown", onKeyDown);
    return () => root.removeEventListener("keydown", onKeyDown);
  }, [enabled, containerRef]);
}

/* ===========================
   Row (horizontal carousel)
=========================== */
const Row = ({ title, items, onOpen }) => {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardW = 280; // approx card + gap
    el.scrollBy({ left: dir * cardW * 3, behavior: "smooth" });
  };

  return (
    <section className="mb-8">
      <div className="flex items-end justify-between px-2 md:px-0">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
          {title}
        </h2>
        <div className="hidden md:flex gap-2">
          <button
            onClick={() => scrollBy(-1)}
            className="p-2 rounded-lg bg-white/60 dark:bg-white/10 border border-white/20 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/20"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scrollBy(1)}
            className="p-2 rounded-lg bg-white/60 dark:bg-white/10 border border-white/20 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/20"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="mt-3 flex gap-3 overflow-x-auto hide-scrollbar scroll-smooth px-2 md:px-0"
      >
        {items.map((it, idx) => (
          <button
            key={`${it.src || it.embedSrc || it.title}-${idx}`}
            onClick={() => onOpen(it)}
            className="group relative w-[260px] shrink-0 rounded-xl overflow-hidden border border-white/20 dark:border-white/10 bg-white/70 dark:bg-white/5 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            title={it.title}
          >
            {/* Thumbnail */}
            <div className="aspect-video relative bg-black/70">
              {it.type === "video" ? (
                it.poster ? (
                  <img
                    src={it.poster}
                    alt={it.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-white/70">
                    <Play className="w-10 h-10" />
                  </div>
                )
              ) : it.type === "image" ? (
                <img
                  src={it.src}
                  alt={it.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : it.poster ? (
                <img
                  src={it.poster}
                  alt={it.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-white/80">
                  <ExternalLink className="w-8 h-8" />
                </div>
              )}

              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors" />
              <div className="absolute bottom-2 left-2 flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-black/70 text-white">
                  {it.type === "video" ? (
                    <Play className="w-3 h-3" />
                  ) : it.type === "image" ? (
                    <ImageIcon className="w-3 h-3" />
                  ) : (
                    <ExternalLink className="w-3 h-3" />
                  )}
                  {it.type === "video"
                    ? it.duration || "Video"
                    : it.type === "image"
                    ? "Image"
                    : "Embed"}
                </span>
              </div>
            </div>

            {/* Meta */}
            <div className="p-3 text-left">
              <div className="line-clamp-1 font-semibold text-gray-900 dark:text-white">
                {it.title}
              </div>
              {it.tags?.length ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {it.tags.map((t, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-200"
                    >
                      <Tag className="w-3 h-3" />
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ===========================
   Modal Viewer (video | image | iframe)
=========================== */
const ModalViewer = ({ open, onClose, item }) => {
  const ref = useRef(null);
  useFocusTrap(open, ref);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !item) return null;

  const isVideo = item.type === "video";
  const isImage = item.type === "image";
  const isEmbed = item.type === "iframe" || item.type === "embed";

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        ref={ref}
        className="w-full max-w-6xl rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 border border-white/20 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {item.title}
            </h3>
            {item.tags?.length ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {item.tags.map((t, i) => (
                  <span
                    key={i}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-200"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-black">
          {isVideo ? (
            <>
              <video
                controls
                playsInline
                preload="metadata"
                autoPlay
                muted
                className="w-full h-auto"
                poster={item.poster}
                crossOrigin={isExternal(item.src) ? "anonymous" : undefined}
                onLoadedMetadata={(e) => {
                  const v = e.currentTarget;
                  console.log("Loaded metadata", { duration: v.duration, src: item.src });
                }}
                onError={(e) => {
                  console.error("Video failed to load", e?.currentTarget?.error);
                }}
              >
                <source src={item.src} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="px-4 py-2 text-xs text-gray-400">
                If the video won’t load, try{" "}
                <a className="underline" href={item.src} target="_blank" rel="noreferrer">
                  opening the file directly
                </a>.
              </div>
            </>
          ) : isImage ? (
            <img src={item.src} alt={item.title} className="w-full h-auto" />
          ) : isEmbed ? (
            <div className="w-full aspect-video">
              <iframe
                src={item.embedSrc || item.src}
                className="w-full h-full border-0"
                title={item.title}
                allow="fullscreen; clipboard-read; clipboard-write; autoplay; encrypted-media"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          ) : null}
        </div>

        {item.description ? (
          <div className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
            {item.description}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ===========================
   Main Page
=========================== */
const LearningHub = () => {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [diagnostic, setDiagnostic] = useState("");

  // Fetch & normalize manifest
  useEffect(() => {
    let mounted = true;
    fetch(toPublic(MANIFEST_URL), { cache: "no-cache" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        const normalized = {
          ...j,
          rows: (j.rows || []).map((row) => ({
            ...row,
            items: (row.items || []).map((it) => ({
              ...it,
              src: toPublic(it.src),
              poster: toPublic(it.poster),
              embedSrc: toPublic(it.embedSrc),
            })),
          })),
          hero: { ...j.hero, background: toPublic(j.hero?.background) },
        };
        if (mounted) setData(normalized);
      })
      .catch((e) => setError(`Failed to load manifest: ${e.message}`));
    return () => {
      mounted = false;
    };
  }, []);

  // Fallback if manifest missing
  const fallback = useMemo(
    () => ({
      hero: {
        title: "UAS Learning Hub",
        subtitle: "Drop a manifest.json in /public/Learning to configure rows.",
        background: toPublic("/Learning/hero.jpg"),
      },
      rows: [
        {
          title: "Sample",
          items: [
            {
              type: "video",
              title: "Sample Video",
              src: toPublic("/Learning/sample.mp4"),
              poster: toPublic("/Learning/sample.jpg"),
              tags: ["Demo"],
            },
            {
              type: "image",
              title: "Sample Image",
              src: toPublic("/Learning/sample.png"),
              tags: ["Demo"],
            },
            {
              type: "iframe",
              title: "FlippingBook (Demo)",
              embedSrc: MAGAZINE_URL,
              poster: toPublic("/Learning/magazine_poster.jpg"),
              tags: ["Magazine"],
            },
          ],
        },
      ],
    }),
    []
  );

  const content = data || fallback;

  // Search filter
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return content.rows || [];
    return (content.rows || [])
      .map((row) => ({
        ...row,
        items: (row.items || []).filter((it) => {
          const inTitle = it.title?.toLowerCase().includes(q);
          const inTags = (it.tags || []).some((t) => String(t).toLowerCase().includes(q));
          return inTitle || inTags;
        }),
      }))
      .filter((row) => (row.items || []).length > 0);
  }, [content, query]);

  // HEAD-check (optional) to provide useful diagnostics
  const handleOpen = async (it) => {
    setDiagnostic("");
    try {
      if (it.type === "video" && it.src) {
        const r = await fetch(it.src, { method: "HEAD" });
        if (!r.ok) {
          setDiagnostic(`Cannot fetch video (HTTP ${r.status}) → ${it.src}`);
        } else {
          const ct = (r.headers.get("content-type") || "").toLowerCase();
          if (!ct.startsWith("video/")) {
            setDiagnostic(`Wrong Content-Type: ${ct} (expected video/*) → ${it.src}`);
          }
          const ranges = (r.headers.get("accept-ranges") || "").toLowerCase();
          if (!ranges.includes("bytes")) {
            console.warn("No Accept-Ranges header; scrubbing may be limited:", it.src);
          }
        }
      }
    } catch (e) {
      setDiagnostic(`Video check failed: ${e?.message || e}`);
    }
    setActive(it); // still open; modal includes a direct-link fallback
  };

  return (
    <div className="flex-1 flex flex-col min-h-full">
      {/* Utilities / page-scoped CSS */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <div className="relative h-[220px] md:h-[320px] w-full overflow-hidden rounded-none">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${content.hero?.background || toPublic("/Learning/hero.jpg")})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-white/0 dark:to-neutral-900/0" />
        <div className="absolute bottom-6 left-4 md:left-8 right-4">
          <h1 className="text-2xl md:text-4xl font-extrabold text-white drop-shadow">
            {content.hero?.title || "UAS Learning Hub"}
          </h1>
          {content.hero?.subtitle ? (
            <p className="mt-1 text-white/90">{content.hero.subtitle}</p>
          ) : null}
          <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex gap-2">
              <a
                href="#rows"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/90 hover:bg-white text-gray-900 text-sm font-semibold"
              >
                <Play className="w-4 h-4" />
                Start Learning
              </a>
              <a
                href="#about"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white backdrop-blur"
              >
                <Info className="w-4 h-4" />
                About
              </a>
            </div>

            {/* Search */}
            <div className="sm:ml-auto w-full sm:w-[340px]">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search titles or tags…"
                className="w-full rounded-xl border px-3 py-2 outline-none
                           placeholder:text-neutral-300
                           focus:ring-2 focus:ring-white/70
                           bg-white/90 text-gray-900
                           dark:bg-white/10 dark:text-white dark:border-white/10 dark:placeholder:text-neutral-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Diagnostic banner */}
      {diagnostic && (
        <div className="mx-3 md:mx-6 mt-4 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800/50">
          {diagnostic}
        </div>
      )}

      {/* Magazine (Full-width iframe embed) */}
      <section className="px-3 md:px-6 py-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
            Monthly Magazine
          </h2>
          <a
            href={MAGAZINE_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline dark:text-blue-400"
            title="Open in new tab"
          >
            Open in new tab <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        <div className="rounded-2xl overflow-hidden border border-gray-200/60 dark:border-white/10 shadow">
          <div className="w-full" style={{ height: "70vh" }}>
            <iframe
              src={MAGAZINE_URL}
              className="w-full h-full border-0"
              title="Learning Magazine"
              allow="fullscreen; clipboard-read; clipboard-write; autoplay; encrypted-media"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Rows */}
      <main id="rows" className="px-3 md:px-6 pb-6">
        {error ? (
          <div className="mb-6 rounded-xl border border-red-300/60 bg-red-50 p-3 text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800/50">
            {error}
          </div>
        ) : null}

        {(filteredRows || []).map((row, i) => (
          <Row key={i} title={row.title} items={row.items || []} onOpen={handleOpen} />
        ))}

        {!error && filteredRows.length === 0 && (
          <div className="rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4 text-center">
            <p className="text-gray-700 dark:text-gray-300">
              No matches. Try a different search term.
            </p>
          </div>
        )}
      </main>

      {/* Footer / About */}
      <section id="about" className="px-3 md:px-6 pb-10">
        <div className="rounded-2xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
            About this hub
          </h3>
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            Add media under <code className="font-mono">public/Learning</code> and describe it in{" "}
            <code className="font-mono">manifest.json</code>. Supported types:{" "}
            <code className="font-mono">"video"</code>,{" "}
            <code className="font-mono">"image"</code>,{" "}
            <code className="font-mono">"iframe"</code>/<code className="font-mono">"embed"</code>.
          </p>
        </div>
      </section>

      {/* Modal Viewer */}
      <ModalViewer open={!!active} onClose={() => setActive(null)} item={active} />
    </div>
  );
};

export default LearningHub;
